import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentUser } from "@/lib/auth";
import type { User } from "@/lib/auth";
import {
  FileText,
  Code,
  DollarSign,
  TrendingUp,
  Calendar,
  ExternalLink,
  Map,
  ChevronDown,
} from "lucide-react";
import Navbar from "@/components/shared/layout/navbar";
import DashboardContainer from "@/components/shared/layout/DashboardContainer";
import ProjectOverview from "@/components/shared/ProjectOverview";
import DocumentCard from "@/components/shared/dashboard/DocumentCard";
import DocumentGrid from "@/components/shared/dashboard/DocumentGrid";
import ProjectSidebar from "@/components/shared/ProjectSidebar";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import CollapsibleWidget from "@/components/shared/CollapsibleWidget";
import { getMainWidgets, getSidebarWidgets } from "@/lib/widgetregistry";
import type { SectionVisibility } from "@/lib/widgetregistry";
import IntroVideoWidget from "@/components/widgets/IntroVideoWidget";
import { useNavigate } from "react-router-dom";

interface Project {
  id: string;
  name: string;
  tagline?: string;
  thumbnail?: string;
  stage?: string;
  status?: string;
  funding_target?: string;
  current_raise?: string;
  access_level: "visible" | "locked";
  show_funding_progress: boolean;
}

interface Document {
  id: string;
  name: string;
  document_type: string;
  is_interactive: boolean;
  file_url?: string;
}

interface ProjectMetrics {
  funding_target?: string;
  current_raise?: string;
  stage?: string;
  status?: string;
  arr?: string;
  target_arr?: string;
  users?: string;
  margin?: string;
  burn_rate?: string;
  runway?: string;
  staff_number?: string;
  sites?: string;
}

export default function ProjectDetail() {
  const [user, setUser] = useState<User | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [projectMetrics, setProjectMetrics] = useState<ProjectMetrics | null>(
    null
  );
  const navigate = useNavigate();
  const [adminNote, setAdminNote] = useState<string>("");
  const [adminNoteDate, setAdminNoteDate] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sectionVisibility, setSectionVisibility] = useState<SectionVisibility>(
    {
      show_documents: true,
      show_site_pipeline: true,
      show_media_gallery: true,
      show_competition: true,
      show_research: true,
      show_press: true,
      show_metrics: true,
      show_funding: true,
      show_admin_note: true,
      show_action_buttons: true,
      show_intro_video: true,
      show_directory: true,
      show_outreach: true,
      show_value_add: true,
      show_eoi: true,
      show_investor_reports: true,
      show_exemplars: true,
      show_market_activity: true,
    }
  );

  // Track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    documents: false,
    site_pipeline: false,
    competition: false,
    research: false,
    press: false,
  });

  const handleBackToDashboard = () => {
    navigate("/investor/dashboard");
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    fetchProjectData();
  }, []);

  const fetchProjectData = async () => {
    try {
      setLoading(true);

      // Get current user
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        setError("User not found");
        return;
      }
      setUser(currentUser);

      // Get project ID from URL
      const pathParts = window.location.pathname.split("/");
      const projectId = pathParts[pathParts.length - 1];

      // Fetch project with access level and visibility settings
      const { data: projectData, error: projectError } = await supabase
        .from("user_project_access")
        .select(
          `
          access_level,
          show_funding_progress,
          show_funding,
          show_documents,
          show_site_pipeline,
          show_media_gallery,
          show_competition,
          show_research,
          show_press,
           show_exemplars,
           show_market_activity,
          show_metrics,
          show_admin_note,
          show_action_buttons,
          show_intro_video, 
          show_directory,
          show_outreach,
          show_value_add,
          show_eoi,
          show_investor_reports,
          projects (
            id,
            name,
            tagline,
            thumbnail,
            stage,
            status,
            funding_target,
            current_raise
          )
        `
        )
        .eq("user_id", currentUser.id)
        .eq("project_id", projectId)
        .single();

      if (projectError) throw projectError;

      const proj = projectData.projects as any;
      setProject({
        id: proj.id,
        name: proj.name,
        tagline: proj.tagline ?? undefined,
        thumbnail: proj.thumbnail ?? undefined,
        stage: proj.stage ?? undefined,
        status: proj.status ?? undefined,
        funding_target: proj.funding_target ?? undefined,
        current_raise: proj.current_raise ?? undefined,
        access_level:
          projectData.access_level === "locked" ? "locked" : "visible",
        show_funding_progress: projectData.show_funding_progress ?? false,
      });

      // Set section visibility
      setSectionVisibility({
        show_documents: projectData.show_documents ?? true,
        show_site_pipeline: projectData.show_site_pipeline ?? true,
        show_media_gallery: projectData.show_media_gallery ?? true,
        show_competition: projectData.show_competition ?? true,
        show_research: projectData.show_research ?? true,
        show_press: projectData.show_press ?? true,
        show_exemplars: projectData.show_exemplars ?? true,
        show_market_activity: projectData.show_market_activity ?? true,
        show_funding: projectData.show_funding ?? true,
        show_metrics: projectData.show_metrics ?? true,
        show_admin_note: projectData.show_admin_note ?? true,
        show_action_buttons: projectData.show_action_buttons ?? true,
        show_intro_video: projectData.show_intro_video ?? true,
        show_directory: projectData.show_directory ?? true,
        show_outreach: projectData.show_outreach ?? true,
        show_value_add: projectData.show_value_add ?? true,
        show_eoi: projectData.show_eoi ?? true,
        show_investor_reports: projectData.show_investor_reports ?? true,
      });

      // Fetch documents for this project
      const { data: docsData, error: docsError } = await supabase
        .from("documents")
        .select("id, name, document_type, is_interactive, file_url")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true });

      if (docsError) throw docsError;
      setDocuments(docsData || []);

      // Fetch project metrics from new table structure
      const { data: metricsData, error: metricsError } = await supabase
        .from("project_metrics")
        .select("*")
        .eq("project_id", projectId)
        .maybeSingle();

      if (metricsError) console.error("Error fetching metrics:", metricsError);
      if (metricsData) {
        setProjectMetrics(metricsData);
      }

      // Fetch admin note
      const { data: noteData, error: noteError } = await supabase
        .from("project_notes")
        .select("content, updated_at")
        .eq("project_id", projectId)
        .eq("note_type", "admin")
        .maybeSingle();

      if (noteError) console.error("Error fetching admin note:", noteError);
      if (noteData) {
        setAdminNote(noteData.content || "");
        // Format date nicely
        const date = new Date(noteData.updated_at);
        const now = new Date();
        const diffDays = Math.floor(
          (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (diffDays === 0) setAdminNoteDate("Today");
        else if (diffDays === 1) setAdminNoteDate("Yesterday");
        else if (diffDays < 7) setAdminNoteDate(`${diffDays} days ago`);
        else setAdminNoteDate(date.toLocaleDateString());
      }
    } catch (err) {
      console.error("Error fetching project:", err);
      setError("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const getDocumentIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      "business-plan": FileText,
      "technical-plan": Code,
      financial: DollarSign,
      "pitch-deck": TrendingUp,
      "quarterly-report": Calendar,
      website: ExternalLink,
      research: Map,
      terms: FileText,
    };
    return iconMap[type] || FileText;
  };

  const getDocumentColor = (type: string): any => {
    const colorMap: Record<string, any> = {
      "business-plan": "blue",
      "technical-plan": "purple",
      financial: "green",
      "pitch-deck": "orange",
      "quarterly-report": "pink",
      website: "indigo",
      research: "gray",
      terms: "red",
    };
    return colorMap[type] || "gray";
  };

  const handleDocumentClick = (doc: Document) => {
    if (doc.is_interactive) {
      // Navigate to interactive document viewer
      navigate(`/investor/project/${project?.id}/document/${doc.id}`);
    } else if (doc.file_url) {
      // Open external file in new tab
      window.open(doc.file_url, "_blank");
    }
  };

  const handleRequestMeeting = () => {
    window.location.href =
      "mailto:hello@tcglventures.com?subject=Meeting Request&body=I would like to request a meeting to discuss this project.";
  };

  const handleAskQuestion = () => {
    window.location.href =
      "mailto:hello@tcglventures.com?subject=Project Question&body=I have a question about this project.";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-gray-900">Loading project...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600">{error || "Project not found"}</p>
        </div>
      </div>
    );
  }

  // Build metrics array from project_metrics table
  const metrics: Array<{ label: string; value: string; progress?: number }> =
    [];

  // Calculate funding progress if both values exist
  const calculateProgress = (current: string, target: string): number => {
    try {
      const currentNum =
        parseFloat(current.replace(/[£,K]/g, "")) *
        (current.includes("K") ? 1000 : 1);
      const targetNum =
        parseFloat(target.replace(/[£,K]/g, "")) *
        (target.includes("K") ? 1000 : 1);
      return Math.min(Math.round((currentNum / targetNum) * 100), 100);
    } catch {
      return 0;
    }
  };

  // Add funding metrics ONLY if show_funding is true
  if (sectionVisibility.show_funding && projectMetrics?.funding_target) {
    const progress = projectMetrics.current_raise
      ? calculateProgress(
          projectMetrics.current_raise,
          projectMetrics.funding_target
        )
      : 0;

    metrics.push({
      label: "Funding Target",
      value: projectMetrics.funding_target,
      progress: progress,
    });
  }

  if (sectionVisibility.show_funding && projectMetrics?.current_raise) {
    const progress = projectMetrics.funding_target
      ? calculateProgress(
          projectMetrics.current_raise,
          projectMetrics.funding_target
        )
      : 0;

    metrics.push({
      label: "Current Raise",
      value: projectMetrics.current_raise,
      progress: progress,
    });
  }

  // Add other metrics without progress bars (always show if show_metrics is true)
  if (projectMetrics?.stage) {
    metrics.push({ label: "Stage", value: projectMetrics.stage });
  }
  if (projectMetrics?.status) {
    metrics.push({ label: "Status", value: projectMetrics.status });
  }
  if (projectMetrics?.arr) {
    metrics.push({ label: "ARR", value: projectMetrics.arr });
  }
  if (projectMetrics?.target_arr) {
    metrics.push({ label: "Target ARR", value: projectMetrics.target_arr });
  }
  if (projectMetrics?.users) {
    metrics.push({ label: "Users", value: projectMetrics.users });
  }
  if (projectMetrics?.margin) {
    metrics.push({ label: "Margin", value: projectMetrics.margin });
  }
  if (projectMetrics?.burn_rate) {
    metrics.push({ label: "Burn Rate", value: projectMetrics.burn_rate });
  }
  if (projectMetrics?.runway) {
    metrics.push({ label: "Runway", value: projectMetrics.runway });
  }
  if (projectMetrics?.staff_number) {
    metrics.push({ label: "Staff", value: projectMetrics.staff_number });
  }
  if (projectMetrics?.sites) {
    metrics.push({ label: "Sites", value: projectMetrics.sites });
  }

  // Get widgets from registry
  const mainWidgets = getMainWidgets();
  const sidebarWidgets = getSidebarWidgets();

  return (
    <div className="min-h-screen bg-blue-50">
      {user && <Navbar user={user} />}

      <ProjectOverview
        name={project.name}
        tagline={project.tagline}
        thumbnail={project.thumbnail}
        stage={project.stage}
        accessLevel={project.access_level}
        onBack={handleBackToDashboard}
      />

      <DashboardContainer>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 py-4 sm:py-6">
          {/* Main Content Column - Collapsible widgets */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Document Library - Collapsible */}
            {sectionVisibility.show_documents && (
              <Card variant="bordered">
                <CardHeader color="blue">
                  <div className="flex items-center justify-between w-full">
                    <h2 className="text-lg sm:text-xl font-bold text-black">
                      Document Library
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Summary items related to the project business plan
                    </p>
                    <button
                      onClick={() => toggleSection("documents")}
                      className="p-1 hover:bg-white/50 rounded transition-colors"
                    >
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${
                          expandedSections.documents ? "rotate-180" : ""
                        }`}
                        strokeWidth={3}
                      />
                    </button>
                  </div>
                </CardHeader>
                {expandedSections.documents && (
                  <CardContent>
                    {documents.length === 0 ? (
                      <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
                        No documents available yet
                      </div>
                    ) : (
                      <DocumentGrid>
                        {documents.map((doc) => (
                          <DocumentCard
                            key={doc.id}
                            name={doc.name}
                            icon={getDocumentIcon(doc.document_type)}
                            available={project.access_level === "visible"}
                            isInteractive={doc.is_interactive}
                            fileUrl={doc.file_url}
                            color={getDocumentColor(doc.document_type)}
                            onClick={() => handleDocumentClick(doc)}
                          />
                        ))}
                      </DocumentGrid>
                    )}
                  </CardContent>
                )}
              </Card>
            )}

            {/* Dynamic Main Content Widgets from Registry */}
            {mainWidgets.map((widget) => {
              const isVisible = sectionVisibility[widget.visibilityKey];
              if (!isVisible) return null;

              return (
                <CollapsibleWidget
                  key={widget.id}
                  projectId={project.id}
                  component={widget.component}
                  isExpanded={expandedSections[widget.id] || false}
                  onToggle={() => toggleSection(widget.id)}
                  headerColor={widget.headerColor}
                />
              );
            })}
          </div>

          {/* Sidebar Column - NOT collapsible */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* INTRO VIDEO WIDGET - FIRST AT TOP */}
            {sectionVisibility.show_intro_video && (
              <IntroVideoWidget projectId={project.id} />
            )}

            {/* PROJECT SIDEBAR - SECOND */}
            {sectionVisibility.show_metrics && (
              <ProjectSidebar
                metrics={metrics}
                adminNote={
                  sectionVisibility.show_admin_note
                    ? adminNote || undefined
                    : undefined
                }
                lastUpdated={
                  sectionVisibility.show_admin_note
                    ? adminNoteDate || undefined
                    : undefined
                }
                showFundingProgress={project.show_funding_progress}
                onRequestMeeting={
                  sectionVisibility.show_action_buttons
                    ? handleRequestMeeting
                    : undefined
                }
                onAskQuestion={
                  sectionVisibility.show_action_buttons
                    ? handleAskQuestion
                    : undefined
                }
              />
            )}

            {/* Dynamic Sidebar Widgets from Registry (EXCLUDING intro_video) */}
            {sidebarWidgets
              .filter((widget) => widget.id !== "intro_video")
              .map((widget) => {
                const isVisible = sectionVisibility[widget.visibilityKey];
                if (!isVisible) return null;

                const WidgetComponent = widget.component;
                return (
                  <WidgetComponent key={widget.id} projectId={project.id} />
                );
              })}
          </div>
        </div>
      </DashboardContainer>
    </div>
  );
}
